import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Box, Grid, TextField, Button,
    Checkbox, FormControlLabel, Typography, Divider, Stack, Chip, Tooltip, Autocomplete,
    MenuItem, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import SaveIcon from '@mui/icons-material/Save';
import { API } from '../App';
import SecureEntryList from './SecureEntryList';

dayjs.locale('de');

function formatNr(nr) {
    if (nr == null) return '';
    return `x${String(nr).padStart(4, '0')}`;
}

function SectionHeader({ title }) {
    return (
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            {title}
        </Typography>
    );
}

export default function JobModal({ modalRef, onSaved }) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [open, setOpen] = useState(false);
    const [job, setJob] = useState({});
    const [creator, setCreator] = useState('');
    const [secureEntries, setSecureEntries] = useState([]);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [userNames, setUserNames] = useState([]);
    const [pdfBlockOpen, setPdfBlockOpen] = useState(false);
    const initialSnapshotRef = useRef('');

    const buildSnapshot = (j, c, sec) => JSON.stringify({
        title: j.title || '',
        description: j.description || '',
        start: j.start ? (j.start.toISOString ? j.start.toISOString() : j.start) : null,
        place: j.place || '',
        contact: j.contact || '',
        executer: j.executer || '',
        comment: j.comment || '',
        creator: c || '',
        done: !!j.done,
        permit: {
            work: !!j.permit?.work, fire: !!j.permit?.fire,
            drive: !!j.permit?.drive, secure: !!j.permit?.secure
        },
        securelist: sec || []
    });

    const isDirty = () => buildSnapshot(job, creator, secureEntries) !== initialSnapshotRef.current;

    useEffect(() => {
        if (modalRef) modalRef.current = { handleOpen, handleClose, setJobState, isOpen: () => open };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalRef, open]);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        (async () => {
            const res = await API.getUserNames();
            if (!cancelled && res?.success) {
                setUserNames(Array.isArray(res.msg) ? res.msg : []);
            }
        })();
        return () => { cancelled = true; };
    }, [open]);

    const handleOpen = (data = {}) => {
        setErrors({});
        let nextJob, nextCreator, nextSecure;
        const isExisting = !!(data._id || data.nr);
        if (Object.keys(data).length === 0) {
            nextJob = {
                nr: null,
                start: dayjs(),
                permit: { work: false, fire: false, drive: false, secure: false },
                securelist: []
            };
            nextSecure = [];
            nextCreator = API.user?.description?.trim() || API.user?.name || '';
        } else {
            nextJob = {
                ...data,
                start: data.start ? dayjs(data.start) : dayjs(),
                created: data.created ? dayjs(data.created) : null,
                permit: data.permit || { work: false, fire: false, drive: false, secure: false },
                securelist: data.securelist || []
            };
            nextSecure = Array.isArray(data.securelist) ? data.securelist : [];
            nextCreator = isExisting ? (data.creator || '') : (API.user?.description?.trim() || API.user?.name || '');
        }
        setJob(nextJob);
        setSecureEntries(nextSecure);
        setCreator(nextCreator);
        initialSnapshotRef.current = buildSnapshot(nextJob, nextCreator, nextSecure);
        setOpen(true);
    };

    const handleClose = () => { setOpen(false); setErrors({}); };

    const setJobState = (jobState, customCreator = null) => {
        setJob(jobState);
        setCreator(customCreator || API.user?.name || '');
    };

    const validate = () => {
        const e = {};
        if (!job.title?.trim()) e.title = 'Arbeitsanweisung ist erforderlich';
        if (!job.description?.trim()) e.description = 'Maßnahme ist erforderlich';
        if (!job.start) e.start = 'Arbeitsaufnahme ist erforderlich';
        if (!job.place?.trim()) e.place = 'Ort/AKZ ist erforderlich';
        if (!job.contact?.trim()) e.contact = 'Ansprechpartner TVM ist erforderlich';
        if (!job.executer?.trim()) e.executer = 'Ausführender ist erforderlich';
        if (!creator?.trim()) e.creator = 'Verfasser ist erforderlich';
        return e;
    };

    const persist = async () => {
        const vErrors = validate();
        if (Object.keys(vErrors).length > 0) { setErrors(vErrors); return { success: false }; }

        setSaving(true);
        const jobToSave = {
            title: job.title,
            description: job.description,
            start: job.start ? job.start.toISOString() : null,
            nr: job.nr,
            place: job.place,
            contact: job.contact,
            executer: job.executer,
            comment: job.comment || '',
            creator,
            done: !!job.done,
            permit: {
                work: !!job.permit?.work,
                fire: !!job.permit?.fire,
                drive: !!job.permit?.drive,
                secure: !!job.permit?.secure
            },
            securelist: secureEntries
        };

        const op = (job.nr || job._id) ? 'updateJob' : 'createJob';
        const res = await API[op](jobToSave);
        setSaving(false);
        if (res?.success) {
            initialSnapshotRef.current = buildSnapshot(job, creator, secureEntries);
            if (onSaved) onSaved();
        } else {
            setErrors({ form: res?.msg || 'Speichern fehlgeschlagen.' });
        }
        return res || { success: false };
    };

    const handleSave = async () => {
        const res = await persist();
        if (res?.success) handleClose();
    };

    const handleDownloadPdf = async () => {
        if (isDirty()) { setPdfBlockOpen(true); return; }
        const res = await API.downloadJobPdf(job.nr);
        if (!res.success) setErrors({ form: res.msg || 'PDF konnte nicht erstellt werden.' });
    };

    const handleSaveAndDownload = async () => {
        const res = await persist();
        if (!res?.success) return;
        setPdfBlockOpen(false);
        const dl = await API.downloadJobPdf(job.nr);
        if (!dl.success) setErrors({ form: dl.msg || 'PDF konnte nicht erstellt werden.' });
    };

    const setPermit = (key) => (e) => setJob({ ...job, permit: { ...job.permit, [key]: e.target.checked } });

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth="md"
                fullScreen={fullScreen}
                PaperProps={{ sx: { backgroundImage: 'none' } }}
            >
                <DialogTitle sx={{ pr: 6 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {job.nr ? 'Auftrag bearbeiten' : 'Neuen Auftrag erstellen'}
                        </Typography>
                        {job.nr && (
                            <Chip
                                size="small"
                                label={formatNr(job.nr)}
                                sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {job.created && (
                            <Tooltip title={`Erstellt: ${dayjs(job.created).format('DD.MM.YYYY HH:mm')}`}>
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto !important', mr: 4 }}>
                                    {dayjs(job.created).format('DD.MM.YYYY HH:mm')}
                                </Typography>
                            </Tooltip>
                        )}
                    </Stack>
                    <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 12, top: 12 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    {errors.form && (
                        <Typography color="error" sx={{ mb: 2 }}>
                            {errors.form}
                        </Typography>
                    )}

                    <SectionHeader title="Kopfdaten" />
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid size={12}>
                            <DateTimePicker
                                label="Arbeitsaufnahme"
                                value={job.start}
                                onChange={(v) => setJob({ ...job, start: v })}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: 'medium',
                                        error: !!errors.start,
                                        helperText: errors.start
                                    }
                                }}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                fullWidth
                                required
                                size="medium"
                                label="Ort / AKZ"
                                value={job.place || ''}
                                onChange={(e) => setJob({ ...job, place: e.target.value })}
                                error={!!errors.place}
                                helperText={errors.place}
                            />
                        </Grid>
                        <Grid size={12}>
                            <Autocomplete
                                freeSolo
                                selectOnFocus
                                handleHomeEndKeys
                                size="medium"
                                options={userNames}
                                value={job.contact || ''}
                                onChange={(_, v) => {
                                    const value = typeof v === 'string' ? v : (v?.description || v?.name || '');
                                    setJob({ ...job, contact: value });
                                }}
                                onInputChange={(_, v) => setJob({ ...job, contact: v })}
                                getOptionLabel={(opt) => {
                                    if (typeof opt === 'string') return opt;
                                    return opt?.description || opt?.name || '';
                                }}
                                filterOptions={(opts, params) => {
                                    const term = params.inputValue.toLowerCase();
                                    if (!term) return opts;
                                    return opts.filter((o) =>
                                        (o.description || '').toLowerCase().includes(term) ||
                                        (o.name || '').toLowerCase().includes(term)
                                    );
                                }}
                                isOptionEqualToValue={(opt, v) => (opt?.description || opt?.name || opt) === (v?.description || v?.name || v)}
                                renderOption={(props, option) => {
                                    const { key, ...rest } = props;
                                    const display = option.description || option.name;
                                    const aside = option.description ? option.name : null;
                                    return (
                                        <li key={key || option.name} {...rest}>
                                            <Stack spacing={0.25}>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{display}</Typography>
                                                {aside && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        @{aside}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </li>
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        required
                                        fullWidth
                                        size="medium"
                                        label="Ansprechpartner TVM"
                                        error={!!errors.contact}
                                        helperText={errors.contact}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={12}>
                            <Autocomplete
                                freeSolo
                                selectOnFocus
                                handleHomeEndKeys
                                size="medium"
                                options={userNames}
                                value={job.executer || ''}
                                onChange={(_, v) => {
                                    const value = typeof v === 'string' ? v : (v?.description || v?.name || '');
                                    setJob({ ...job, executer: value });
                                }}
                                onInputChange={(_, v) => setJob({ ...job, executer: v })}
                                getOptionLabel={(opt) => {
                                    if (typeof opt === 'string') return opt;
                                    return opt?.description || opt?.name || '';
                                }}
                                filterOptions={(opts, params) => {
                                    const term = params.inputValue.toLowerCase();
                                    if (!term) return opts;
                                    return opts.filter((o) =>
                                        (o.description || '').toLowerCase().includes(term) ||
                                        (o.name || '').toLowerCase().includes(term)
                                    );
                                }}
                                isOptionEqualToValue={(opt, v) => (opt?.description || opt?.name || opt) === (v?.description || v?.name || v)}
                                renderOption={(props, option) => {
                                    const { key, ...rest } = props;
                                    const display = option.description || option.name;
                                    const aside = option.description ? option.name : null;
                                    return (
                                        <li key={key || option.name} {...rest}>
                                            <Stack spacing={0.25}>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{display}</Typography>
                                                {aside && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        @{aside}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </li>
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        required
                                        fullWidth
                                        size="medium"
                                        label="Ausführender"
                                        error={!!errors.executer}
                                        helperText={errors.executer}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                fullWidth
                                size="medium"
                                label="Verfasser"
                                value={(() => {
                                    if (!creator) return '';
                                    const match = userNames.find((u) => u.name === creator);
                                    return match?.description?.trim() || creator;
                                })()}
                                disabled
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                fullWidth
                                select
                                size="medium"
                                label="Status"
                                value={job.done ? 'done' : 'open'}
                                onChange={(e) => setJob({ ...job, done: e.target.value === 'done' })}
                            >
                                <MenuItem value="open">Offen</MenuItem>
                                <MenuItem value="done">Erledigt</MenuItem>
                            </TextField>
                        </Grid>
                    </Grid>

                    <Divider sx={{ mb: 2 }} />

                    <SectionHeader title="Arbeitsanweisung" />
                    <Stack spacing={2} sx={{ mb: 3 }}>
                        <TextField
                            fullWidth
                            required
                            size="medium"
                            label="Arbeitsanweisung (Titel)"
                            multiline
                            minRows={2}
                            value={job.title || ''}
                            onChange={(e) => setJob({ ...job, title: e.target.value })}
                            error={!!errors.title}
                            helperText={errors.title}
                        />
                        <TextField
                            fullWidth
                            required
                            size="medium"
                            label="Maßnahme"
                            multiline
                            minRows={4}
                            value={job.description || ''}
                            onChange={(e) => setJob({ ...job, description: e.target.value })}
                            error={!!errors.description}
                            helperText={errors.description}
                        />
                        <TextField
                            fullWidth
                            size="medium"
                            label="Anmerkungen"
                            multiline
                            minRows={2}
                            value={job.comment || ''}
                            onChange={(e) => setJob({ ...job, comment: e.target.value })}
                        />
                    </Stack>

                    <Divider sx={{ mb: 2 }} />

                    <SectionHeader title="Erlaubnisscheine" />
                    <Grid container spacing={1} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControlLabel
                                control={<Checkbox checked={!!job.permit?.work} onChange={setPermit('work')} />}
                                label="Freigabeschein / Arbeitserlaubnis"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControlLabel
                                control={<Checkbox checked={!!job.permit?.drive} onChange={setPermit('drive')} />}
                                label="Befahrerlaubnis"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControlLabel
                                control={<Checkbox checked={!!job.permit?.fire} onChange={setPermit('fire')} />}
                                label="Feuererlaubnis"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControlLabel
                                control={<Checkbox checked={!!job.permit?.secure} onChange={setPermit('secure')} />}
                                label="Sicherungsschein"
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ mb: 2 }} />

                    <SectionHeader title="Sicherungsliste" />
                    <SecureEntryList
                        secureEntries={secureEntries}
                        onUpdate={setSecureEntries}
                        jobNr={job.nr}
                    />
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
                    <Box>
                        {job.nr && (
                            <Button
                                variant="outlined"
                                startIcon={<PictureAsPdfOutlinedIcon />}
                                onClick={handleDownloadPdf}
                            >
                                PDF herunterladen
                            </Button>
                        )}
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Button onClick={handleClose}>Abbrechen</Button>
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? 'Speichert…' : 'Speichern'}
                        </Button>
                    </Stack>
                </DialogActions>
            </Dialog>

            <Dialog open={pdfBlockOpen} onClose={() => setPdfBlockOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Ungespeicherte Änderungen</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Bitte zuerst speichern. Das PDF wird sonst ohne Ihre aktuellen Änderungen erzeugt.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setPdfBlockOpen(false)} disabled={saving}>Abbrechen</Button>
                    <Button onClick={handleSaveAndDownload} variant="contained" startIcon={<SaveIcon />} disabled={saving}>
                        {saving ? 'Speichert…' : 'Speichern & PDF'}
                    </Button>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
}
